/* eslint-disable import/no-duplicates */
import React, { useEffect, useRef, useState } from 'react';
import type { FC } from 'react';
import { useIntl, defineMessages } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { useExpandSideNav } from '@atlaskit/navigation-system/layout/side-nav';
import {
	PopoverProvider,
	PopoverTarget,
	PopoverContent,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightBody,
	SpotlightFooter,
	SpotlightActions,
	SpotlightPrimaryAction,
	SpotlightSecondaryAction,
	SpotlightCard,
} from '@atlaskit/spotlight';

import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { HOME } from '@confluence/named-routes/entry-points/routes.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { useTransitionId } from '@confluence/route-manager/entry-points/RouteState.ts';
import { HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import { useIsEligibleForNthUserHomeOnboardingTakeoverV2HomeNudge } from '../hooks/useIsEligibleForNthUserHomeOnboardingTakeoverV2HomeNudge.ts';

const i18n = defineMessages({
	homeSpotlightHeader: {
		id: 'experiment-home-onboarding-takeover-v2.nth-user-home-nudge.title',
		defaultMessage: 'More ways to get started 💡',
		description: 'Message that gets displayed in spotlight header section',
	},
	homeSpotlightText: {
		id: 'experiment-home-onboarding-takeover-v2.nth-user-home-nudge.description',
		defaultMessage: 'When you’re ready, discover tailored onboarding suggestions in For You.',
		description: 'Message that gets displayed in spotlight message',
	},
	homeSpotlightCloseButtonText: {
		id: 'experiment-home-onboarding-takeover-v2.nth-user-home-nudge.button-got-it',
		defaultMessage: 'Got it',
		description: 'button to dismiss spotlight that gets displayed in spotlight message',
	},
	homeSpotlightTryItButtonText: {
		id: 'experiment-home-onboarding-takeover-v2.nth-user-home-nudge.button-go-to-for-you',
		defaultMessage: 'Go to For You',
		description: 'button to navigate to For You that gets displayed in spotlight message',
	},
});

type NthUserHomeOnboardingTakeoverV2HomeNudgeProps = {
	position?: 'bottom-start' | 'right-start';
	children?: React.ReactNode;
};

export const NthUserHomeOnboardingTakeoverV2HomeNudge: FC<
	NthUserHomeOnboardingTakeoverV2HomeNudgeProps
> = ({ children }) => {
	const [isHomeNudgeHidden, setIsHomeNudgeHidden] = useState(true);
	const wasEligibleForOnboardingNudgeRef = useRef(false);
	const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

	const { formatMessage } = useIntl();
	const { setOnboardingState } = useSetOnboardingState();

	const { push } = useRouteActions();
	// This is the onboarding state hidden
	const { isEligibleForHomeNudge, isOnHome, error, isHomeNudgeScheduledAt } =
		useIsEligibleForNthUserHomeOnboardingTakeoverV2HomeNudge();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { useChoreographer } = useChoreographerAssets();
	const expandSideNav = useExpandSideNav();
	const transitionId = useTransitionId();
	const transitionIdRef = useRef<number | null>(null);

	const { startMessage, stopMessage } = useChoreographer(
		'nth-user-home-onboarding-takeover-v2-home-nudge',
		{
			start: () => setIsHomeNudgeHidden(false),
			stop: () => setIsHomeNudgeHidden(true),
		},
	);

	const handleDismiss = () => {
		setIsHomeNudgeHidden(true);
		void stopMessage();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'nthUserHomeOnboardingTakeoverV2HomeNudgeDismiss',
				source: 'onboarding',
			},
		}).fire();
	};

	const handleGoHome = () => {
		push(HOME.toUrl());
		void stopMessage();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'nthUserHomeOnboardingTakeoverV2HomeNudgeGoHome',
				source: 'onboarding',
			},
		}).fire();
	};

	useEffect(() => {
		const showNudge = () => {
			void setOnboardingState({
				key: HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS.hasSeenHomeNudge,
				value: 'true',
			});
			void startMessage();
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					action: 'viewed',
					name: 'nthUserHomeOnboardingTakeoverV2HomeNudge',
				},
			}).fire();
			expandSideNav();
		};

		if (
			isEligibleForHomeNudge &&
			isHomeNudgeHidden &&
			(!wasEligibleForOnboardingNudgeRef.current || transitionIdRef.current !== transitionId)
		) {
			// first time about to show nudge, wait 2mins seconds before showing
			// Upon subsequent reloads or navigation, show the nudge immediately
			if (!timeoutRef.current && !isHomeNudgeScheduledAt) {
				timeoutRef.current = setTimeout(
					() => {
						showNudge();
						clearTimeout(timeoutRef.current);
					},
					2 * 1000 * 60,
				);
				void setOnboardingState({
					key: HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS.isHomeNudgeScheduledAt,
					value: new Date().toISOString(),
				});
			} else if (
				isHomeNudgeScheduledAt &&
				(transitionId === 0 || transitionIdRef.current !== transitionId)
			) {
				showNudge();
				clearTimeout(timeoutRef.current);
			}
			wasEligibleForOnboardingNudgeRef.current = true;
		}
		transitionIdRef.current = transitionId;
	}, [
		createAnalyticsEvent,
		isEligibleForHomeNudge,
		isHomeNudgeHidden,
		setOnboardingState,
		startMessage,
		stopMessage,
		expandSideNav,
		transitionId,
		isHomeNudgeScheduledAt,
	]);

	useEffect(() => {
		return () => {
			clearTimeout(timeoutRef.current);
		};
	}, []);

	const isNudgeHidden =
		!isHomeNudgeHidden && (wasEligibleForOnboardingNudgeRef.current || isEligibleForHomeNudge);

	return (
		<PopoverProvider>
			<PopoverTarget>{children}</PopoverTarget>
			<PopoverContent
				dismiss={handleDismiss}
				placement="right-end"
				isVisible={isNudgeHidden && !isOnHome}
			>
				<SpotlightCard testId="spotlight">
					<SpotlightHeader>
						<SpotlightHeadline>{formatMessage(i18n.homeSpotlightHeader)}</SpotlightHeadline>
						<SpotlightControls>
							<SpotlightDismissControl onClick={handleDismiss} />
						</SpotlightControls>
					</SpotlightHeader>
					<SpotlightBody>{formatMessage(i18n.homeSpotlightText)}</SpotlightBody>
					<SpotlightFooter>
						<SpotlightActions>
							<SpotlightPrimaryAction onClick={handleGoHome}>
								{formatMessage(i18n.homeSpotlightTryItButtonText)}
							</SpotlightPrimaryAction>
							<SpotlightSecondaryAction onClick={handleDismiss}>
								{formatMessage(i18n.homeSpotlightCloseButtonText)}
							</SpotlightSecondaryAction>
						</SpotlightActions>
					</SpotlightFooter>
				</SpotlightCard>
			</PopoverContent>
			{error && <ErrorDisplay error={error} />}
		</PopoverProvider>
	);
};
